import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Button } from './Button';
import { Input } from './Input';
import { Upload, Plus, X } from 'lucide-react';

export const MultiImageUploader = ({ label, value, onChange, placeholder, previewHeight = "h-32", allowMultiple = false }) => {
    const [uploading, setUploading] = useState(false);
    const [inputValue, setInputValue] = useState('');

    // Parse value to array
    const getImages = () => {
        if (!value) return [];
        try {
            let parsed;
            if (value.startsWith('[')) parsed = JSON.parse(value);
            else parsed = [value];
            return parsed.filter(url => 
                url && 
                typeof url === 'string' &&
                url.trim() !== '' &&
                url !== 'null' &&
                url !== 'undefined' &&
                !url.includes('example.com') &&
                !url.includes('voorbeeld.nl')
            );
        } catch { 
            return [value].filter(url => 
                url && 
                typeof url === 'string' &&
                url.trim() !== '' &&
                url !== 'null' &&
                url !== 'undefined' &&
                !url.includes('example.com') &&
                !url.includes('voorbeeld.nl')
            ); 
        }
    };

    const images = getImages();

    const handleAddImage = (url) => {
        if (!url) return;

        let newImages;
        if (allowMultiple) {
            newImages = [...images, url];
            onChange(JSON.stringify(newImages));
        } else {
            // Single mode: Replace
            onChange(url);
        }
        setInputValue('');
    };

    const handleRemoveImage = (index) => {
        if (allowMultiple) {
            const newImages = images.filter((_, i) => i !== index);
            onChange(newImages.length > 0 ? JSON.stringify(newImages) : '');
        } else {
            onChange('');
        }
    };

    const handleUpload = async (e) => {
        try {
            setUploading(true);
            const file = e.target.files[0];
            if (!file) return;

            // Generate unique filename
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
            const filePath = `uploads/${fileName}`;

            // Upload to 'images' bucket 
            const { error: uploadError } = await supabase.storage
                .from('images')
                .upload(filePath, file);

            if (uploadError) {
                console.error('Upload native error:', uploadError);
                if (uploadError.message.includes('Bucket not found') || uploadError.error === 'Bucket not found') {
                    alert('⚠️ Fout: Opslag emmer "images" bestaat niet.\n\nGa naar Supabase Dashboard -> Storage -> New Bucket "images" (Public).');
                } else if (uploadError.message.includes('new row violates row-level security') || uploadError.statusCode === '42501') {
                    alert('🔒 Fout: Geen rechten.\n\nGa naar Supabase Dashboard -> Storage -> Policies -> images -> New Policy (Authenticated Insert).');
                } else {
                    alert('Upload fout: ' + (uploadError.message || 'Onbekend probleem'));
                }
                throw uploadError;
            }

            // Get Public URL
            const { data } = supabase.storage
                .from('images')
                .getPublicUrl(filePath);

            if (data) {
                handleAddImage(data.publicUrl);
            }
        } catch (error) {
            console.error('Upload Error:', error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="space-y-4">
            {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}

            {/* Image List / Grid */}
            {images.length > 0 && (
                <div className={`grid ${allowMultiple ? 'grid-cols-2 gap-2' : 'grid-cols-1'}`}>
                    {images.map((img, idx) => (
                        <div key={idx} className={`relative group bg-gray-100 rounded-lg border border-gray-200 overflow-hidden ${previewHeight}`}>
                            <img src={img} alt={`Preview ${idx}`} className="w-full h-full object-contain p-2" />
                            <button
                                onClick={() => handleRemoveImage(idx)}
                                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                title="Verwijder"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Input Area */}
            {(allowMultiple || images.length === 0) && (
                <div className="flex gap-2 items-center">
                    <Input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={placeholder || (allowMultiple ? "Voeg URL toe..." : "Afbeelding URL")}
                        className="flex-1"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddImage(inputValue);
                            }
                        }}
                    />

                    {/* Add URL Button (only shown if something typed) */}
                    {inputValue && (
                        <Button type="button" onClick={() => handleAddImage(inputValue)} variant="secondary">
                            <Plus size={16} />
                        </Button>
                    )}

                    {/* Upload File Button */}
                    <div className="relative">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleUpload}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-50"
                            disabled={uploading}
                            title="Upload een afbeelding"
                        />
                        <Button type="button" variant="secondary" className="whitespace-nowrap relative z-0" disabled={uploading}>
                            {uploading ? '...' : <><Upload size={16} className="mr-2" /> Upload</>}
                        </Button>
                    </div>
                </div>
            )}

            {allowMultiple && <p className="text-xs text-gray-400">Je kunt meerdere afbeeldingen toevoegen. Ze verschijnen onder elkaar.</p>}
        </div>
    );
};
